import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  ArrowLeft, Database, Search, RefreshCw, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const STATUS_META = {
  synced:  { label: 'Synced',  className: 'bg-green-100 text-green-700' },
  partial: { label: 'Partial', className: 'bg-amber-100 text-amber-800' },
  missing: { label: 'Missing', className: 'bg-red-100 text-red-700' },
};

const RestoreStatus = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [query, setQuery] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/restore-status`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load restore status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); /* eslint-disable-next-line */ }, []);

  const triggerRestore = async () => {
    if (!window.confirm('Run a force restore from the bundled fixture? This will upsert every fixture document into the live DB and is safe to repeat.')) return;
    setRestoring(true);
    try {
      const res = await axios.post(`${API}/api/admin/restore-demo-data`, null, { headers });
      const summary = res.data?.totals || res.data;
      toast.success(`Restore complete — inserted ${summary?.inserted ?? '?'}, updated ${summary?.updated ?? '?'}`);
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-gray-500">
        Loading restore status…
      </div>
    );
  }

  if (!data) return null;

  const rows = (data.rows || []).filter((r) =>
    !query || r.collection.toLowerCase().includes(query.toLowerCase())
  );

  const exportedAt = data.fixture_exported_at
    ? new Date(data.fixture_exported_at).toLocaleString('en-GB')
    : 'unknown';

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Database className="h-6 w-6 text-blue-600" /> Data Sync Status
            </h1>
            <p className="text-sm text-gray-500">
              Fixture exported on <strong>{exportedAt}</strong> · {data.total_collections} collections tracked
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchStatus} data-testid="refresh-status-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            onClick={triggerRestore}
            disabled={restoring}
            data-testid="force-restore-btn"
          >
            {restoring ? 'Restoring…' : 'Force Restore Now'}
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card data-testid="summary-synced">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-green-700" />
            <div>
              <div className="text-2xl font-semibold text-green-700">{data.summary?.synced ?? 0}</div>
              <div className="text-xs text-gray-500">Synced</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="summary-partial">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
            <div>
              <div className="text-2xl font-semibold text-amber-700">{data.summary?.partial ?? 0}</div>
              <div className="text-xs text-gray-500">Partial (live &lt; fixture)</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="summary-missing">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-red-600" />
            <div>
              <div className="text-2xl font-semibold text-red-700">{data.summary?.missing ?? 0}</div>
              <div className="text-xs text-gray-500">Missing (live = 0)</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="mb-4 relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter collections…"
          className="pl-9"
          data-testid="filter-input"
        />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Per-collection breakdown</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Collection</th>
                  <th className="text-right px-4 py-2 font-medium">Fixture</th>
                  <th className="text-right px-4 py-2 font-medium">Live</th>
                  <th className="text-right px-4 py-2 font-medium">Δ</th>
                  <th className="text-center px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody data-testid="status-table-body">
                {rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.synced;
                  const delta = r.live_count - r.fixture_count;
                  return (
                    <tr key={r.collection} className="border-t hover:bg-gray-50" data-testid={`row-${r.collection}`}>
                      <td className="px-4 py-2 font-mono text-xs">{r.collection}</td>
                      <td className="px-4 py-2 text-right">{r.fixture_count}</td>
                      <td className="px-4 py-2 text-right">{r.live_count < 0 ? '—' : r.live_count}</td>
                      <td className={`px-4 py-2 text-right text-xs ${delta < 0 ? 'text-red-600' : delta > 0 ? 'text-gray-500' : 'text-gray-500'}`}>
                        {delta === 0 ? '0' : (delta > 0 ? `+${delta}` : delta)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <Badge className={`${meta.className} text-xs`}>{meta.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan="5" className="text-center py-6 text-gray-500">No matching collections</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-gray-500 mt-4">
        Tip: Missing collections will be auto-topped-up on the next backend restart. Use <strong>Force Restore Now</strong> to apply
        immediately without restarting.
      </p>
    </div>
  );
};

export default RestoreStatus;
